/**
 * F09 v2 — the modal shell every verification form
 * dialog shares. Dialogs are mounted only while open, so each opening starts
 * from a clean form (no stale reason / ID number survives a close).
 */
import { type ReactNode } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Button, Modal } from '@bravemove/ui-web';
import { VerificationErrorBanner } from './VerificationBits';

export function FormModal({
  title,
  description,
  onClose,
  onConfirm,
  confirmLabel,
  destructive = false,
  busy = false,
  confirmDisabled = false,
  error,
  testId,
  footerExtra,
  hideConfirm = false,
  children,
}: {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly confirmLabel: string;
  readonly destructive?: boolean;
  readonly busy?: boolean;
  readonly confirmDisabled?: boolean;
  readonly error?: unknown;
  readonly testId: string;
  readonly footerExtra?: ReactNode;
  readonly hideConfirm?: boolean;
  readonly children: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <Modal
      open
      onClose={busy ? () => undefined : onClose}
      title={title}
      description={description}
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            data-testid={`${testId}-cancel`}
          >
            {t('admin:verification.common.cancel')}
          </Button>
          {footerExtra}
          {hideConfirm ? null : (
            <Button
              variant={destructive ? 'destructive' : 'primary'}
              onClick={onConfirm}
              loading={busy}
              disabled={confirmDisabled}
              data-testid={`${testId}-confirm`}
            >
              {confirmLabel}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4" data-testid={testId}>
        {error ? <VerificationErrorBanner error={error} testId={`${testId}-error`} /> : null}
        {children}
      </div>
    </Modal>
  );
}
